import React, { MouseEvent, ReactNode } from 'react';
import Breadcrumb from '../../Breadcrumb';
import CustomEditorItemComponent from '../../custom/CustomEditorItemComponent';
import CustomTabv2 from '../../custom/CustomTabv2';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import { RootState } from '../../../redux/store';
import { enterComponent, leaveComponent } from '../../../redux/actions/ComponentActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import VelocityVariableEditorComponent from './editor/VelocityVariableEditorComponent';
import VelocityVariableAccessComponent from './VelocityVariableAccessComponent';
import { VelocityVariableState } from '../../../redux/reducers/VelocityVariableReducer';
import { changeVelocityVariable, changeVelocityVariableDirty, changeVelocityVariableIsUpdate, clearVelocityVariable } from '../../../redux/actions/VelocityVariableActions';
import ComponentName from '../../../constant/ComponentName';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../../constant/CollectionNames';
import { VelocityVariable } from '../../../domain/enrichment/EnrichmentDomain';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import CustomLoading from '../../custom/CustomLoading';
import { TransactionJsonModel } from '../../../domain/stream/transaction/TransactionDomains';
import VelocityVariableService from '../../../service/VelocityVariableService';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import { customConfirm } from '../../custom/CustomPopupConfirm';
import { confirmUndeployWithImpact } from '../../deploy/UndeployImpactDialog';
import LanguageUtil from '../../../util/LanguageUtil';
import RoleHelper from '../../../RoleHelper';
import ComponentAuditLog from '../../ComponentAuditLog';
import { clearPage } from '../../../redux/actions/PageActions';
import StandardEditorContainerComponent, { StandardEditorContainerComponentArgs } from '../../../helpers/StandardEditorContainerComponent';
import ComponentErrorLog from '../../ComponentErrorLog';
import { isStringValid } from '../../../util/Generator';
import MessageUtil from '../../../util/MessageUtil';
import { saveDatesToLocalStorage } from '../../custom/custom-time-picker/TimePickerContainer';
import { EnrichedComponentCategory } from '../../../domain/category/CategoryOperationDomains';
import { ConfirmPopup } from 'primereact/confirmpopup';
import eq = query.eq;

class VelocityVariableEditorContainer extends StandardEditorContainerComponent<Props, State, VelocityVariable> {
  public state = new State();

  private velocityVariableSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;
  private initialDirtyResetTimer: ReturnType<typeof setTimeout> | undefined;
  private syncDirtyResetTimer: ReturnType<typeof setTimeout> | undefined;
  private isInitialDirtyResetScheduled: boolean = false;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly velocityVariableService: VelocityVariableService = ServiceContainer.getInstance().get(ServiceNames.VELOCITY_VARIABLE);

  constructor(props: Props) {
    const args: StandardEditorContainerComponentArgs = {
      canPreview: true,
      component: ComponentName.ENRICHMENT,
      icon: 'enrichment',
    };
    super(props, args);
  }

  public render(): ReactNode {
    const {
      velocityVariable: { velocityVariable, isUpdate, isDirty },
    } = this.props;
    const { loading } = this.state;
    if (loading) {
      return <CustomLoading />;
    }
    return (
      <div className={'flex flex-col h-full w-full overflow-y-hidden'}>
        <Breadcrumb />
        <ConfirmPopup />
        <CustomEditorItemComponent
          name={velocityVariable.name}
          description={velocityVariable.description}
          status={velocityVariable?.stage || 'DRAFT'}
          component={ComponentName.VELOCITY}
          isUpdate={isUpdate}
          icon={'enrichment'}
          onChangeName={(name) => this.onChangeName(name)}
          onChangeDescription={(description) => this.onChangeDescription(description)}
          onSave={() => this.onSave()}
          onRemove={(e) => this.onRemove(e)}
          onDeploy={(e) => this.onDeploy(e)}
          onUndeploy={(e) => this.onUndeploy(e)}
          disableSave={!RoleHelper.canEdit('EnrichmentVelocity')}
          disableRemove={!RoleHelper.canEdit('EnrichmentVelocity')}
          disableNameChange={!RoleHelper.canEdit('EnrichmentVelocity')}
          disableDescriptionChange={!RoleHelper.canEdit('EnrichmentVelocity')}
          disableDeploy={!RoleHelper.canDeploy('EnrichmentVelocity') || isDirty}
          disableUndeploy={!RoleHelper.canDeploy('EnrichmentVelocity')}
        />
        <div className={'flex flex-col overflow-y-scroll h-full w-full '}>
          <CustomTabv2 tab={this.getTabs()} />
        </div>
      </div>
    );
  }

  public async componentWillUnmount(): Promise<void> {
    const { clearVelocityVariable } = this.props;
    if (this.initialDirtyResetTimer) {
      clearTimeout(this.initialDirtyResetTimer);
    }
    if (this.syncDirtyResetTimer) {
      clearTimeout(this.syncDirtyResetTimer);
    }
    clearVelocityVariable();
    await super.componentWillUnmount();
  }

  public componentDidUpdate(prevProps: Props) {
    const {
      velocityVariable: { isUpdate },
      changeVelocityVariableDirty,
    } = this.props;
    if (!prevProps.velocityVariable.isUpdate && isUpdate && !this.isInitialDirtyResetScheduled) {
      this.isInitialDirtyResetScheduled = true;
      this.initialDirtyResetTimer = setTimeout(() => {
        changeVelocityVariableDirty(false);
      }, 1200);
    }
  }

  private resubscribeTracker = async () => {
    this.setState({ loading: true });
    if (this.computation == undefined) {
      await this.startTracker();
    } else {
      this.computation?.onStop(this.startTracker);
      await this.stopTracker();
    }
  };

  protected _startTracker = async () => {
    const variableId = this.getVariableIdFromRoute();
    if (variableId == undefined) {
      this.setState({ loading: false });
    }
    const velocityVariableQuery = query.QueryBuilder.select().from(CollectionNames.ENRICHMENTS);
    this.velocityVariableSubscription = subscribe.Formica.subscribe(CollectionNames.ENRICHMENTS, velocityVariableQuery.build());
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.velocityVariableSubscription]);
  };

  protected tracker = async () => {
    const {
      changeVelocityVariableIsUpdate,
      changeVelocityVariable,
      changeVelocityVariableDirty,
      velocityVariable: { velocityVariable: _velocityVariable },
    } = this.props;
    if (this.velocityVariableSubscription?.ready()) {
      const variableId = this.getVariableIdFromRoute();
      if (variableId == undefined) {
        changeVelocityVariableIsUpdate(false);
        RoleHelper.setCurrentAccess('Edit');
        this.setState({ loading: false });
        return;
      }
      const velocityVariableQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.ENRICHMENTS).where(eq('name', variableId)).build();
      const velocityVariable: VelocityVariable | undefined = await this.repository.find(velocityVariableQuery);
      if (velocityVariable == undefined) {
        return;
      }
      changeVelocityVariable(velocityVariable);
      changeVelocityVariableDirty(false);
      if (this.syncDirtyResetTimer) {
        clearTimeout(this.syncDirtyResetTimer);
      }
      this.syncDirtyResetTimer = setTimeout(() => {
        changeVelocityVariableDirty(false);
      }, 1200);
      changeVelocityVariableIsUpdate(true);
      RoleHelper.setCurrentAccess(velocityVariable);
      this.setState({ loading: false, updated: this.state.updated + 1 });
    }
  };

  protected _stopTracker = async () => {
    await this.velocityVariableSubscription?.stop();
    this.computation?.stop();
  };

  private getVariableIdFromRoute = () => {
    const { changeVelocityVariableIsUpdate } = this.props;
    const id = this.props.router.params.id;
    changeVelocityVariableIsUpdate(id != undefined);
    return id;
  };

  private onChangeName(name: string) {
    const {
      velocityVariable: { velocityVariable },
      changeVelocityVariable,
    } = this.props;
    if (isStringValid(name)) {
      velocityVariable.name = name;
      changeVelocityVariable(velocityVariable);
    } else {
      MessageUtil.warning({
        summary: LanguageUtil.inappropriateCharacters(),
      });
    }
  }

  private onChangeDescription(description: string) {
    const {
      velocityVariable: { velocityVariable },
      changeVelocityVariable,
    } = this.props;
    velocityVariable.description = description;
    changeVelocityVariable(velocityVariable);
  }

  private async onSave() {
    const {
      velocityVariable: { velocityVariable, isUpdate },
      changeVelocityVariableIsUpdate,
      lastCategory,
    } = this.props;
    const isSaved = await this.velocityVariableService.save(velocityVariable, isUpdate, lastCategory);
    saveDatesToLocalStorage(velocityVariable.startDate, velocityVariable.endDate);
    if (isSaved) {
      changeVelocityVariableIsUpdate(true);
      if (!isUpdate) await this.switchToUpdate();
    }
  }

  private async onRemove(e: MouseEvent<HTMLElement>) {
    const {
      velocityVariable: { velocityVariable },
      router: { navigate },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        const success = await this.velocityVariableService.delete(velocityVariable.name);
        if (success) {
          navigate(`/enrichment`);
        }
      },
      message: LanguageUtil.get({ id: 'messages.delete1', defaultMessage: 'Are you sure you want to remove?' }),
    });
  }

  private async onDeploy(e: MouseEvent<HTMLElement>) {
    const {
      velocityVariable: { velocityVariable },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        const isSaved = await this.velocityVariableService.save(velocityVariable, true, {} as EnrichedComponentCategory);
        if (!isSaved) {
          return;
        }
        await this.velocityVariableService.deploy(velocityVariable);
      },
      message: LanguageUtil.get({ id: 'messages.confirm.deploy1', defaultMessage: 'Are you sure you want to deploy?' }),
    });
  }

  private async onUndeploy(e: MouseEvent<HTMLElement>) {
    const {
      velocityVariable: { velocityVariable },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        await confirmUndeployWithImpact(ComponentName.VELOCITY, [velocityVariable.name], () =>
          this.velocityVariableService.undeploy(velocityVariable.name),
        );
      },
      message: LanguageUtil.get({ id: 'messages.confirm.undeploy1', defaultMessage: 'Are you sure you want to undeploy?' }),
    });
  }

  private switchToUpdate = async () => {
    const {
      velocityVariable: { velocityVariable },
      enterComponent,
    } = this.props;
    const {
      router: { navigate, params },
    } = this.props;
    if (velocityVariable.name == '') return;
    enterComponent({ component: ComponentName.ENRICHMENT, id: velocityVariable.name });
    navigate(`/enrichment${params.folderId ? `/folder/${params.folderId}` : ''}/velocity/${velocityVariable.name}`, { replace: true });
    setTimeout(() => {
      this.resubscribeTracker();
    });
  };

  protected getTabs = () => {
    const { updated } = this.state;
    return [
      {
        key: 'Editor',
        label: LanguageUtil.get({ id: 'common.tabs.editor', defaultMessage: 'Editor' }),
        content: <VelocityVariableEditorComponent updated={updated} />,
      },
      {
        key: 'Access',
        label: LanguageUtil.get({ id: 'common.tabs.access', defaultMessage: 'Access' }),
        content: <VelocityVariableAccessComponent />,
        visible: RoleHelper.canManage(ComponentName.VELOCITY, true),
      },
      {
        key: 'Audit',
        label: LanguageUtil.get({ id: 'common.tabs.audit', defaultMessage: 'Audit' }),
        content: <ComponentAuditLog component={ComponentName.VELOCITY} documentName={this.props.velocityVariable.velocityVariable.name} canPreview />,
        visible: RoleHelper.canManage(ComponentName.VELOCITY, true) && this.environmentUtil.isAuditLogVisible(),
      },
      {
        key: 'Error',
        label: LanguageUtil.get({ id: 'common.tabs.error', defaultMessage: 'Error' }),
        content: <ComponentErrorLog component={ComponentName.VELOCITY} documentName={this.props.velocityVariable.velocityVariable.name} />,
        visible: RoleHelper.canManage(ComponentName.VELOCITY, true),
      },
    ];
  };

  protected changeItem = (item: VelocityVariable) => {
    const { changeVelocityVariable } = this.props;
    changeVelocityVariable(item);
  };
}

interface Props extends FormicaProps, ReduxProps {}

class State {
  transactions: TransactionJsonModel[] = [];
  streams: VelocityVariable[] = [];
  loading: boolean = true;
  updated: number = 0;
}

const mapStateToProps = (state: RootState) => ({
  velocityVariable: state.velocityVariable as VelocityVariableState,
  lastCategory: state.page.lastCategory,
});

const mapDispatchToProps = {
  enterComponent,
  leaveComponent,
  changeVelocityVariable,
  changeVelocityVariableDirty,
  clearVelocityVariable,
  changeVelocityVariableIsUpdate,
  clearPage,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(VelocityVariableEditorContainer));
